import { useMemo, useState } from 'react'

const PRESET_DAYS = [7, 30, 90, 180, 365]

type PurposeOption = 'general' | 'pro' | 'reporter_claim' | 'custom'

export default function StakingCreateModal({
  open,
  availablePixe,
  onClose,
  onSubmit,
}: {
  open: boolean
  availablePixe: number
  onClose: () => void
  onSubmit: (payload: { amount_pixe: string; lock_seconds: number; purpose: string; metadata?: Record<string, any> }) => Promise<void>
}) {
  const [amount, setAmount] = useState('')
  const [durationMode, setDurationMode] = useState<'preset' | 'custom'>('preset')
  const [days, setDays] = useState<number>(30)
  const [customDays, setCustomDays] = useState('30')
  const [purpose, setPurpose] = useState<PurposeOption>('general')
  const [customPurpose, setCustomPurpose] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parsedAmount = useMemo(() => {
    const n = Number(amount)
    return Number.isFinite(n) ? n : NaN
  }, [amount])
  const activeDays = durationMode === 'preset' ? days : Number(customDays || 0)
  const lockSeconds = Math.round(activeDays * 24 * 60 * 60)

  const normalizedPurpose = useMemo(() => {
    if (purpose === 'custom') {
      const slug = (customPurpose || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
      return slug ? `custom:${slug}` : ''
    }
    return purpose
  }, [customPurpose, purpose])

  const canSubmit =
    Number.isFinite(parsedAmount) &&
    parsedAmount >= 1 &&
    parsedAmount <= availablePixe &&
    Number.isFinite(activeDays) &&
    activeDays >= 1 &&
    activeDays <= 365 &&
    lockSeconds >= 3600 &&
    normalizedPurpose.length > 0

  async function submit() {
    if (!canSubmit || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSubmit({
        amount_pixe: parsedAmount.toString(),
        lock_seconds: lockSeconds,
        purpose: normalizedPurpose,
      })
      setAmount('')
      setError(null)
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to create stake')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="md:w-[640px] w-full bg-neutral-950 border-t md:border border-white/10 rounded-t-2xl md:rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="text-white font-extrabold text-lg">Stake PIXE</div>
            <button onClick={onClose} className="px-2 py-1 rounded-md text-white/90 hover:bg-white/10">x</button>
          </div>

          <div className="mt-2 text-sm text-white/70">Stake PIXE (locks funds for feature access)</div>

          <label className="block mt-4">
            <div className="text-sm text-white/80 mb-1">Amount</div>
            <div className="flex gap-2">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-500/40"
              />
              <button
                type="button"
                onClick={() => setAmount(availablePixe.toString())}
                className="px-3 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10"
              >
                Max
              </button>
            </div>
            <div className="text-xs text-white/60 mt-1">Available: {availablePixe.toLocaleString()} PIXE</div>
          </label>

          <div className="mt-4">
            <div className="text-sm text-white/80 mb-1">Duration</div>
            <div className="flex flex-wrap gap-2">
              {PRESET_DAYS.map((d) => (
                <button
                  type="button"
                  key={d}
                  onClick={() => {
                    setDurationMode('preset')
                    setDays(d)
                  }}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${durationMode === 'preset' && days === d ? 'border-sky-400/50 bg-sky-500/20 text-sky-100' : 'border-white/15 text-white/85 hover:bg-white/10'}`}
                >
                  {d} days
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDurationMode('custom')}
                className={`px-3 py-1.5 rounded-lg border text-sm ${durationMode === 'custom' ? 'border-sky-400/50 bg-sky-500/20 text-sky-100' : 'border-white/15 text-white/85 hover:bg-white/10'}`}
              >
                Custom
              </button>
            </div>
            {durationMode === 'custom' && (
              <input
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                placeholder="Days"
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-500/40"
              />
            )}
          </div>

          <div className="mt-4">
            <div className="text-sm text-white/80 mb-1">Purpose</div>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as PurposeOption)}
              className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-sky-500/40"
            >
              <option value="general">General</option>
              <option value="pro">Pro Access</option>
              <option value="reporter_claim">Reporter Claim</option>
              <option value="custom">Custom</option>
            </select>
            {purpose === 'custom' && (
              <input
                value={customPurpose}
                onChange={(e) => setCustomPurpose(e.target.value)}
                placeholder="custom purpose slug"
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-500/40"
              />
            )}
          </div>

          {error && <div className="mt-3 text-sm text-rose-300">{error}</div>}

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10">Cancel</button>
            <button
              onClick={submit}
              disabled={!canSubmit || busy}
              className={`px-4 py-2 rounded-lg font-semibold ${canSubmit && !busy ? 'bg-sky-600 hover:bg-sky-500 text-white' : 'bg-white/10 text-white/60 cursor-not-allowed'}`}
            >
              {busy ? 'Staking...' : 'Stake PIXE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
